import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingV2 from 'components/LoadingV2'
import PMBSelect from 'components/PMBSelect'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  X,
} from 'react-feather'
import Flatpickr from 'react-flatpickr'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import {
  SelectControlWithTestId,
} from 'components/PMBSelect/SelectInputWithTestId'
import { Badge, Card, Col, Collapse, Row } from 'reactstrap'
import { BUSINESS_BOOKKEEPING, BUSINESS_BOOKKEEPING_COMPANY, MANAGE, READ } from 'utils/subjectActions'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import {
  useLazyGetArusKasQuery,
  useLazyGetArusKasPersonalQuery,
  useGetCategoriesCashFlowQuery,
  useGetCategoriesCashFlowPersonalQuery,
} from './cashFlowApiSlice'
import { setFetchOption, setFilters } from './cashFlowSlice'
import AnnualTab from './tabs/AnnualTab'
import MonthlyTab from './tabs/MonthlyTab'
import ProfitLossCardSection from './tabs/ProfitLossCardSection'
import CashFlowInsights from './tabs/CashFlowInsights'

const CashFlow = () => {
  const dispatch = useDispatch()
  const { filters, tag } = useSelector((s) => s.cashflow)
  const { profile, ownerData } = useSelector((s) => s.dashboard)

  const hasPersonalAbility = useAbilityAccess({ action: READ, subject: BUSINESS_BOOKKEEPING })
  const hasCompanyAbility = useAbilityAccess({ action: READ, subject: BUSINESS_BOOKKEEPING_COMPANY })
  const showSwitcher = hasPersonalAbility && hasCompanyAbility

  const selectedYearAnnual = filters.selectedYearAnnual
  const selectedMonthYear = filters.selectedMonthYear

  const [getArusKas, res] = useLazyGetArusKasQuery()
  const [getArusKasPersonal, resPersonal] = useLazyGetArusKasPersonalQuery()

  const [dataIncome, setDataIncome] = useState([])
  const [dataExpense, setDataExpense] = useState([])
  const [totalIncome, setTotalIncome] = useState(0)
  const [totalExpense, setTotalExpense] = useState(0)
  const [totalProfit, setTotalProfit] = useState(0)
  const [yearOptionsAnnual, setYearOptionsAnnual] = useState([])
  const [activeTab, setActiveTab] = useState('tahunan')
  const [dataCashFlow, setDataCashFlow] = useState(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isShowFilter, setIsShowFilter] = useState(false)

  // Determine effective type based on abilities
  // Default is personal, fallback to company if only company ability exists
  const effectiveType = !hasPersonalAbility && hasCompanyAbility
    ? 'company'
    : (filters?.selectedType || 'personal')

  const isPersonal = effectiveType === 'personal'
  const userId = isPersonal ? profile?.userId : ownerData?.userId

  const { data: dataCategoryCompany } = useGetCategoriesCashFlowQuery(
    { active: 1, userId: userId, allmanual: 1 },
    { skip: !userId || isPersonal }
  )

  const { data: dataCategoryPersonal } = useGetCategoriesCashFlowPersonalQuery(
    { active: 1, userId: userId, allmanual: 1 },
    { skip: !userId || !isPersonal }
  )

  const dataCategory = isPersonal ? dataCategoryPersonal : dataCategoryCompany
  const categoryOptions = Array.isArray(dataCategory) ? dataCategory.map(item => ({
    label: item.label,
    value: item.id,
    ...item
  })) : []

  const selectedMonth = moment(selectedMonthYear)?.format('YYYY-MM')
  const daysInMonth = moment(selectedMonth, 'YYYY-MM').daysInMonth()

  const startdate =
    activeTab === 'tahunan'
      ? `${selectedYearAnnual?.value || new Date().getFullYear()}-01-01`
      : `${selectedMonth}-01`

  const enddate =
    activeTab === 'tahunan'
      ? `${selectedYearAnnual?.value || new Date().getFullYear()}-12-31`
      : `${selectedMonth}-${daysInMonth}`

  const updatedEndDate = moment(enddate).add(1, 'day').format('YYYY-MM-DD')

  const handleFilterChange = (filter) => {
    dispatch(
      setFilters({
        selectedFilter: filter?.value,
        selectedCategory: null,
      }),
    )
  }

  const handleCategoryChange = (val) => {
    dispatch(
      setFilters({
        selectedCategory: val,
      }),
    )
  }

  const filterOptions = [
    { label: 'Semua catatan', value: 'all' },
    { label: 'Sistem Transaksi', value: 'transaksi' },
    { label: 'Sistem Inventori', value: 'inventory' },
    { label: 'Catat manual', value: 'manual' },
  ]

  // HANDLE IS MOBILE
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const fetchCashFlowData = async () => {
      try {
        const queryOptions = {
          startdate,
          enddate: updatedEndDate,
          filter: filters?.selectedFilter,
          categoryId: filters?.selectedFilter === 'manual' ? filters?.selectedCategory?.offlineID : undefined,
          page: 0,
          size: 10000,
        }

        const resultResponse = effectiveType === 'personal'
          ? await getArusKasPersonal(queryOptions).unwrap()
          : await getArusKas(queryOptions).unwrap()

        const rawData = resultResponse?.items || []
        setDataCashFlow(resultResponse)

        const { totalIncome, totalExpense, incomeByMonth, expenseByMonth } =
          rawData.reduce(
            (acc, item) => {
              const monthIndex = moment(item?.createddate)?.month()

              if (item?.incomeorexpense === 'income') {
                acc.totalIncome += item?.amount
                acc.incomeByMonth[monthIndex] += item?.amount
              } else if (item?.incomeorexpense === 'expense') {
                acc.totalExpense += item?.amount
                acc.expenseByMonth[monthIndex] += item?.amount
              }

              return acc
            },
            {
              totalIncome: 0,
              totalExpense: 0,
              incomeByMonth: Array(12).fill(0),
              expenseByMonth: Array(12).fill(0),
            },
          )

        setTotalIncome(totalIncome)
        setTotalExpense(totalExpense)
        setTotalProfit(totalIncome - totalExpense)
        setDataIncome(incomeByMonth)
        setDataExpense(expenseByMonth)
      } catch (error) {
        console.warn('Error fetching cash flow data', error)
        setDataCashFlow(null)
        setTotalIncome(0)
        setTotalExpense(0)
        setTotalProfit(0)
        setDataIncome(Array(12).fill(0))
        setDataExpense(Array(12).fill(0))
      }
    }

    fetchCashFlowData()
  }, [
    selectedMonthYear,
    selectedYearAnnual,
    tag?.page,
    tag?.size,
    activeTab,
    filters,
  ])

  useEffect(() => {
    const currentYear = new Date().getFullYear()
    const createdAtUser = profile?.createdAt
      ? new Date(profile.createdAt).getFullYear()
      : currentYear

    const years = []
    for (let i = createdAtUser; i <= currentYear; i++) {
      years.push({ value: i, label: String(i) })
    }

    setYearOptionsAnnual(years)

    if (!selectedYearAnnual) {
      dispatch(setFilters({ selectedYearAnnual: years[0] }))
    }
  }, [dispatch, selectedYearAnnual, profile])

  useEffect(() => {
    if (!selectedYearAnnual) {
      const currentYear = new Date().getFullYear()
      const currentYearOption =
        yearOptionsAnnual?.find((option) => option?.value === currentYear) ||
        yearOptionsAnnual[0]

      dispatch(setFilters({ selectedYearAnnual: currentYearOption }))
    }
  }, [dispatch, selectedYearAnnual, yearOptionsAnnual])

  const handleYearChangeAnnual = (selectedOption) => {
    dispatch(setFilters({ selectedYearAnnual: selectedOption }))
  }

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
    dispatch(setFetchOption({ page: 0, size: 10 }))
  }

  const handleChangeMonthYear = (selectedDates) => {
    if (selectedDates.length > 0) {
      const date = selectedDates[0]
      dispatch(setFilters({ selectedMonthYear: date }))
    }
  }

  const handleTypeChange = (type) => {
    dispatch(
      setFilters({
        selectedType: type?.value,
      }),
    )
  }

  // Calculate active filter count
  const activeFilterCount = (filters?.selectedFilter && filters?.selectedFilter !== 'all' ? 1 : 0) +
    (filters?.selectedFilter === 'manual' && filters?.selectedCategory ? 1 : 0)

  // Reset all filters to default
  const handleResetFilter = () => {
    dispatch(setFilters({ selectedFilter: 'all', selectedCategory: null }))
  }

  useEffect(() => {
    if (!selectedMonthYear) {
      dispatch(setFilters({ selectedMonthYear: new Date().toISOString() }))
    }
  }, [dispatch, selectedMonthYear])

  if (effectiveType === 'personal' ? resPersonal?.isLoading : res?.isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Laporan Arus Kas'} />
      </div>

      <Card className="mb-4 p-3" style={{ border: 'none' }}>
        <div className="mb-2 d-flex flex-column gap-3">
          <PMBTabSwitcher
            activeTab={activeTab}
            tabs={[
              { key: 'tahunan', label: 'Tahunan' },
              { key: 'bulanan', label: 'Bulanan' },
            ]}
            onTabChange={handleTabChange}
            testId="cashflow-tab"
          />
        </div>

        <Row className="gy-3 align-items-center justify-content-between mb-4">
          <Col xs="12" md="auto" className="d-flex flex-column flex-md-row align-items-center gap-3 text-center text-md-start">
            <h5 className="mb-0">Laporan Arus Kas</h5>
            {showSwitcher && (
              <div className="d-flex bg-light rounded p-1 border">
                <div
                  className={`px-3 py-1 rounded ${effectiveType !== 'personal' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-muted'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px' }}
                  onClick={() => handleTypeChange({ value: 'company' })}
                >
                  Perusahaan
                </div>
                <div
                  className={`px-3 py-1 rounded ${effectiveType === 'personal' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-muted'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px' }}
                  onClick={() => handleTypeChange({ value: 'personal' })}
                >
                  Personal
                </div>
              </div>
            )}
          </Col>

          <Col xs="12" md="auto">
            <div>
              <div className="d-flex align-items-center gap-2 w-100 flex-md-grow-1">
                {activeFilterCount > 0 && (
                  <div className="flex-shrink-0">
                    <CustomButton
                      onClick={handleResetFilter}
                      color="danger"
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        minWidth: '40px',
                      }}
                    >
                      <X size={16} />
                    </CustomButton>
                  </div>
                )}
                <div className="flex-grow-1">
                  <CustomButton
                    onClick={() => setIsShowFilter((prevState) => !prevState)}
                    color="primary"
                    outline
                    className="w-100 position-relative"
                  >
                    <div className="d-flex align-items-center gap-2 justify-content-center">
                      <Filter size={15} />
                      <P>Filter</P>
                      {activeFilterCount > 0 && (
                        <Badge
                          color="danger"
                          pill
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            fontSize: '10px',
                          }}
                        >
                          {activeFilterCount}
                        </Badge>
                      )}
                      {isShowFilter ? (
                        <ChevronUp size={16} className="ml-2" />
                      ) : (
                        <ChevronDown size={16} className="ml-2" />
                      )}
                    </div>
                  </CustomButton>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Collapse isOpen={isShowFilter}>
          <div
            className="border rounded p-3 bg-light mb-3"
            style={{
              width: '100%',
              transition: 'all 0.3s ease-in-out',
              opacity: isShowFilter ? 1 : 0,
              transform: isShowFilter ? 'translateY(0)' : 'translateY(-10px)',
            }}
          >
            <Row className="g-2">
              <Col xs="12" md="4" lg="4">
                <div
                  className="flex-grow-1"
                  style={{
                    width: '100%',
                  }}
                >
                  {activeTab === 'tahunan' ? (
                    <>
                      <P className="mb-2">Tahun</P>
                      <PMBSelect
                        value={selectedYearAnnual}
                        onChange={handleYearChangeAnnual}
                        options={yearOptionsAnnual}
                        isMulti={false}
                        placeholder="Pilih tahun"
                      />
                    </>
                  ) : (
                    <>
                      <P className="mb-2">Bulan</P>
                      <div
                        className="d-flex align-items-center border rounded bg-white"
                        style={{ height: 42 }}
                      >
                        <Calendar size={17} className="ms-3" />
                        <Flatpickr
                          value={
                            selectedMonthYear
                              ? moment(selectedMonthYear).format('YYYY-MM')
                              : moment().format('YYYY-MM')
                          }
                          onChange={handleChangeMonthYear}
                          data-testid="cashflow-month-picker"
                          options={{
                            altInput: true,
                            altFormat: 'F Y',
                            dateFormat: 'Y-m',
                            defaultDate: selectedMonthYear
                              ? moment(selectedMonthYear).format('YYYY-MM')
                              : moment().format('YYYY-MM'),
                            disableMobile: true,
                            maxDate: moment().format('YYYY-MM'),
                            minDate: profile?.createdAt
                              ? moment(profile.createdAt).format('YYYY-MM-DD')
                              : undefined,
                            plugins: [
                              new MonthSelectPlugin({
                                shorthand: false,
                                dateFormat: 'Y-m',
                                altFormat: 'F Y',
                              }),
                            ],
                          }}
                          className="form-control bg-transparent border-0 shadow-none"
                        />
                      </div>
                    </>
                  )}
                </div>
              </Col>

              <Col xs="12" md="4" lg="4">
                <div
                  style={{
                    width: '100%',
                  }}
                >
                  <P className="mb-2">Jenis</P>
                  <PMBSelect
                    isSearchable={false}
                    onChange={handleFilterChange}
                    options={filterOptions}
                    testId="cashflow-filter-select"
                    value={filterOptions?.find(
                      (e) => e?.value === filters?.selectedFilter,
                    )}
                  />
                </div>
              </Col>

              <Col xs="12" md="4" lg="4">
                <div
                  style={{
                    width: '100%',
                  }}
                >
                  <P className="mb-2">Kategori</P>
                  <Select
                    isClearable
                    isSearchable={false}
                    onChange={handleCategoryChange}
                    options={categoryOptions}
                    value={filters?.selectedCategory}
                    isDisabled={filters?.selectedFilter !== 'manual'}
                    classNamePrefix="select"
                    data-testid="cashflow-filter-category-select"
                    components={{ Control: SelectControlWithTestId }}
                    menuPosition="fixed"
                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    placeholder="Pilih Kategori..."
                  />
                </div>
              </Col>
            </Row>
          </div>
        </Collapse>

        <ProfitLossCardSection
          res={effectiveType === 'personal' ? resPersonal : res}
          totalExpense={totalExpense}
          totalIncome={totalIncome}
          totalProfit={totalProfit}
          items={dataCashFlow?.items}
        />

        {/* <CashFlowInsights dataCashFlow={dataCashFlow} /> */}

        {activeTab === 'tahunan' && (
          <AnnualTab
            dataCashFlow={dataCashFlow}
            dataIncome={dataIncome}
            dataExpense={dataExpense}
            res={effectiveType === 'personal' ? resPersonal : res}
          />
        )}

        {activeTab === 'bulanan' && (
          <MonthlyTab
            selectedMonthYear={selectedMonthYear}
            dataCashFlow={dataCashFlow}
            res={effectiveType === 'personal' ? resPersonal : res}
          />
        )}
      </Card>
    </div>
  )
}

export default CashFlow
